import { fetch as expoFetch } from "expo/fetch";
import { useCallback, useEffect, useState, useTransition } from "react";
import { Platform } from "react-native";
import { DEMO_SESSION_ANALYSIS_ENDPOINT } from "../../qa-search";
import { type DemoExamsType, mockedSessionAnalysis } from "./constants";

type Params = {
	sessionId: number;
	examName: string;
	tenantName: string;
	outputLanguage?: string;
	demoExamId: string;
	userData?: Record<string, unknown>;
	goldStandardData?: Record<string, unknown>;
};

const isMockedData = true;
export const useSessionAnalysis = ({
	sessionId,
	examName,
	tenantName,
	outputLanguage,
	demoExamId,
	userData,
	goldStandardData,
}: Params) => {
	const [sessionAnalysis, setSessionAnalysis] = useState("");
	const [loading, setLoading] = useState(false);
	const [streaming, setStreaming] = useState(false);
	const [_isPending, startTransition] = useTransition();
	const isMediQuest = demoExamId === "mediquest-1";

	// biome-ignore lint/correctness/useExhaustiveDependencies: // TODO fix me later
	const fetchSessionAnalysis = useCallback(async () => {
		if (isMockedData && !isMediQuest) {
			setStreaming(true);
			const words =
				mockedSessionAnalysis[demoExamId as DemoExamsType].split(" ");
			let currentText = "";
			let wordIndex = 0;

			const interval = setInterval(() => {
				if (wordIndex < words.length) {
					if (Platform.OS !== "web") {
						// Add 4 words at a time for non-web platforms
						currentText += (wordIndex > 0 ? " " : "") + words[wordIndex];
						for (let i = 1; i < 4; i++) {
							if (wordIndex + i < words.length) {
								currentText += ` ${words[wordIndex + i]}`;
							}
						}
						wordIndex = Math.min(wordIndex + 4, words.length);
					} else {
						// Add 1 word at a time for web
						currentText += (wordIndex > 0 ? " " : "") + words[wordIndex];
						wordIndex++;
					}
					startTransition(() => {
						setSessionAnalysis(currentText);
					});
				} else {
					clearInterval(interval);
					setStreaming(false);
				}
			}, 5);

			return;
		}
		if (sessionId) {
			setLoading(true);
			const response = await expoFetch(DEMO_SESSION_ANALYSIS_ENDPOINT, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					Accept: "text/event-stream",
				},
				body: JSON.stringify({
					sessionId,
					examName,
					tenantName,
					outputLanguage,
					//TODO: Make this dynamic
					agentId: 742,
					input: {
						goldStandard: goldStandardData,
						userData,
					},
				}),
			});
			const reader = await response.body?.getReader();
			let result = "";
			const encoder = new TextDecoder();
			let done = false;
			setLoading(false);
			setStreaming(true);
			while (!done && reader) {
				const { value, done: _done } = await reader.read();
				done = _done;
				if (value) {
					result += encoder.decode(value, { stream: !done });
				}
				setSessionAnalysis(result);
			}
			setStreaming(false);
		}
	}, [sessionId]);

	useEffect(() => {
		fetchSessionAnalysis();
	}, [fetchSessionAnalysis]);
	return {
		sessionAnalysis,
		loading,
		streaming,
	};
};
